import { Component, ViewChild, AfterViewInit, OnDestroy, ChangeDetectorRef, input, output, effect, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Menu, MenuModule } from 'primeng/menu';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { IconComponent, IconSource, IconSourceEnum } from '../icon/icon.component';
import { IconSize } from '../icon/icon.types';
import { ToggleComponent } from "../toggle/toggle.component";
import { ButtonComponent } from '../button/button.component';
import { SearchBarComponent } from 'src/app/@shared/components/search-bar/search-bar.component';

export enum MenuItemType {
  MULTI_SELECT = 'MULTI_SELECT',
  SINGLE_SELECT = 'SINGLE_SELECT',
  TOGGLE = 'TOGGLE',
  BUTTON = 'BUTTON',
}

export interface CustomMenuItem {
  iconSource?: IconSourceEnum;
  icon?: string;
  iconSize?: IconSize;
  label: string;
  description?: string;
  value?: any;
  data?: any;
  subOptions?: CustomMenuItem[];
  callback?: (item: CustomMenuItem) => void;
  type?: MenuItemType;
  toggleState?: boolean;
  selectionTree?: Record<string, any>;
  disabled?: boolean;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonClass?: string;
  buttonLoading?: boolean;
  buttonCallback?: (item: CustomMenuItem) => void;
  class?: string;
  /** When true the item acts as a non-interactive section separator / group label. */
  isGroupHeader?: boolean;
  /** Optional number shown as a circular badge indicator before the label. */
  badgeNumber?: number;
  /** When true, a search bar is shown when navigated into this item's sub-options. */
  searchable?: boolean;
}

@Component({
  selector: 'app-menu',
  standalone: true,
  imports: [CommonModule, MenuModule, IconComponent, ToggleSwitchModule, FormsModule, ToggleComponent, ButtonComponent, SearchBarComponent],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss'
})
export class MenuComponent implements AfterViewInit, OnDestroy {

  private static openMenus = new Set<MenuComponent>();

  visible = input<boolean>(false);
  items = input<CustomMenuItem[]>([]);
  selectedValues = input<CustomMenuItem[]>([]);
  search = input<boolean>(false);
  class = input<string>('');
  itemTemplate = input<TemplateRef<any> | null>(null);
  itemSelected = output<CustomMenuItem>();
  visibleChange = output<boolean>();

  @ViewChild('menu') menu!: Menu;

  displayedItems: CustomMenuItem[] = [];
  navigationStack: CustomMenuItem[][] = [];
  parentItemStack: CustomMenuItem[] = [];
  private targetEvent: Event | null = null;
  private _isVisible: boolean = false;
  searchQuery: string = '';
  showSubMenuSearch: boolean = false;

  IconSourceEnum = IconSourceEnum;
  MenuItemType = MenuItemType;

  constructor(private cdr: ChangeDetectorRef) {
    effect(() => {
      const visibleValue = this.visible();
      if (this.menu) {
        if (visibleValue) {
          this.menu.show(new Event('click'));
          this._isVisible = true;
        } else {
          this.menu.hide();
          this._isVisible = false;
        }
      }
    });

    effect(() => {
      // Track items() so this effect re-runs when the source list changes.
      this.items();
      const itemsToShow = this.getCurrentLevelItems();
      if (this.searchQuery.trim() === '') {
        this.displayedItems = itemsToShow;
      } else {
        this.displayedItems = this.filterItems(itemsToShow, this.searchQuery);
      }

      if (this._isVisible && this.menu?.container) {
        this.repositionMenu();
      }
    });
  }

  ngAfterViewInit() {
    this.displayedItems = this.items();
  }

  toggle(event: Event) {
    this.targetEvent = event;
    if (this.menu) {
      // Close every other open menu before toggling this one
      MenuComponent.openMenus.forEach(m => { if (m !== this) m.hide(); });
      this.menu.toggle(event);
    }
  }

  hide() {
    this.visibleChange.emit(false);
    this.menu.hide();
  }

  ngOnDestroy() {
    MenuComponent.openMenus.delete(this);
  }

  onVisibilityChange(visible: boolean) {
    this._isVisible = visible;
    this.visibleChange.emit(visible);

    if (visible) {
      MenuComponent.openMenus.add(this);
    } else {
      MenuComponent.openMenus.delete(this);
      this.resetNavigation();
      this.targetEvent = null;
      this.searchQuery = '';
    }
  }

  isItemSelected(item: CustomMenuItem): boolean {
    if (item.isGroupHeader || item.type === MenuItemType.TOGGLE || item.type === MenuItemType.BUTTON) {
      return false;
    }
    return item.value !== undefined && this.selectedValues().some(selectedValue => selectedValue.value === item.value);
  }

  onToggleChange(item: CustomMenuItem, value: boolean) {
    item.toggleState = value;
    if (item.callback) {
      item.callback(item);
    }

    let selectionTree: any;

    if (this.parentItemStack.length > 0) {
      const parentItem = this.parentItemStack[this.parentItemStack.length - 1];
      const parentValue = parentItem?.value ?? parentItem?.label ?? 'selected_values';
      selectionTree = {
        [parentValue]: item.toggleState
      };
    } else {
      selectionTree = {
        [item.value]: item.toggleState
      }
    }

    item.selectionTree = selectionTree;
    this.itemSelected.emit(item);
  }

  onButtonClick(event: Event, item: CustomMenuItem) {
    event.stopPropagation();

    if (item.disabled) {
      return;
    }

    if (item.buttonCallback) {
      item.buttonCallback(item);
    }

    this.itemSelected.emit(item);
  }

  onItemClick(event: Event, item: CustomMenuItem) {
    event.stopPropagation();

    if (item.disabled || item.isGroupHeader) {
      return;
    }

    if (item.type === MenuItemType.BUTTON) {
      return;
    }

    if (item.subOptions && item.subOptions.length > 0) {
      this.navigateToSubOptions(item);
    }
    else if (item.type !== MenuItemType.TOGGLE) {
      let selectionTree: any;

      if (this.parentItemStack.length > 0) {
        const parentItem = this.parentItemStack[this.parentItemStack.length - 1];
        const parentValue = parentItem?.value ?? parentItem?.label ?? 'selected_values';
        item = { ...item, type: parentItem?.type ?? MenuItemType.MULTI_SELECT };
        selectionTree = {
          [parentValue]: [item.value]
        };
      } else {
        selectionTree = {
          [item.value]: [item.value]
        };
      }

      item.selectionTree = selectionTree;
      this.itemSelected.emit(item);

      if (item.callback) {
        item.callback(item);
      }

      this.hide();
    }
  }

  private navigateToSubOptions(item: CustomMenuItem) {
    this.navigationStack.push([...this.displayedItems]);
    this.parentItemStack.push(item);
    // Reset any active search so the submenu opens showing all of its options.
    this.searchQuery = '';
    this.displayedItems = item.subOptions || [];
    // Show the submenu search bar only when this item opted in via `searchable`.
    this.showSubMenuSearch = !!item.searchable;

    // Force change detection first
    this.cdr.detectChanges();

    // Reposition the menu after content changes
    this.repositionMenu();
  }

  navigateBack(event: Event) {
    event.stopPropagation();

    if (this.navigationStack.length > 0) {
      this.navigationStack.pop();
      this.parentItemStack.pop();
      // Clear the search and show the (now parent) level's full set of items.
      this.showSubMenuSearch = false;
      this.searchQuery = '';
      this.displayedItems = this.getCurrentLevelItems();
      this.cdr.detectChanges();
      this.repositionMenu();
    }
  }

  /** The full, unfiltered set of items for the level currently being shown. */
  private getCurrentLevelItems(): CustomMenuItem[] {
    if (this.parentItemStack.length > 0) {
      const parentItem = this.parentItemStack[this.parentItemStack.length - 1];
      return parentItem.subOptions || [];
    }
    return this.items();
  }

  private repositionMenu() {
    // Wait for DOM to update
    setTimeout(() => {
      if (this.menu && this.menu.container) {
        // Get the menu container element
        const container = this.menu.container as HTMLElement;

        // Get the target element (trigger button)
        const target = this.targetEvent?.target as HTMLElement;

        if (container && target) {
          // Get positions
          const targetRect = target.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;

          // Calculate available space
          const spaceBelow = viewportHeight - targetRect.bottom;
          const spaceAbove = targetRect.top;
          const spaceRight = viewportWidth - targetRect.right;
          const spaceLeft = targetRect.left;

          // Reset transform
          container.style.transform = '';

          // Position vertically
          if (spaceBelow >= containerRect.height || spaceBelow >= spaceAbove) {
            // Position below
            container.style.top = `${targetRect.bottom}px`;
            container.style.bottom = 'auto';
          } else {
            // Position above
            container.style.bottom = `${viewportHeight - targetRect.top}px`;
            container.style.top = 'auto';
          }

          // Position horizontally
          if (spaceRight >= containerRect.width || spaceRight >= spaceLeft) {
            // Position to the right
            container.style.left = `${targetRect.left}px`;
            container.style.right = 'auto';
          } else {
            // Position to the left
            container.style.right = `${viewportWidth - targetRect.right}px`;
            container.style.left = 'auto';
          }

          // Ensure menu doesn't go off-screen
          const updatedRect = container.getBoundingClientRect();

          if (updatedRect.bottom > viewportHeight) {
            container.style.maxHeight = `${spaceBelow - 10}px`;
            container.style.overflowY = 'auto';
          }

          if (updatedRect.right > viewportWidth) {
            container.style.maxWidth = `${spaceRight - 10}px`;
          }
        }
      }
    }, 0);
  }

  onSearchChange(query: string) {
    this.searchQuery = query.toLowerCase();
    const itemsToFilter = this.getCurrentLevelItems();
    if (this.searchQuery.trim() === '') {
      this.displayedItems = itemsToFilter;
    } else {
      this.displayedItems = this.filterItems(itemsToFilter, this.searchQuery);
    }
  }

  private filterItems(items: CustomMenuItem[], query: string): CustomMenuItem[] {
    return items.filter(item => {
      if (item.isGroupHeader) return false;
      const labelMatch = item.label.toLowerCase().includes(query);
      const descriptionMatch = item.description?.toLowerCase().includes(query) || false;
      return labelMatch || descriptionMatch;
    });
  }

  private resetNavigation() {
    this.navigationStack = [];
    this.parentItemStack = [];
    this.displayedItems = this.items();
    this.searchQuery = '';
    this.showSubMenuSearch = false;
  }

  get currentParentItem(): CustomMenuItem | null {
    return this.parentItemStack.length > 0
      ? this.parentItemStack[this.parentItemStack.length - 1]
      : null;
  }

  get isInSubMenu(): boolean {
    return this.navigationStack.length > 0;
  }

  get isVisible(): boolean {
    return this._isVisible;
  }
}
